import type { AvatarId } from '@teaplus/shared';
import type { ReactNode } from 'react';

// Original drawings on a 96 x 96 grid. Heroes and the film and TV avatars are our own characters
// that nod to a genre or story, never a studio's character, name or likeness.

export interface AvatarArt {
  label: string;
  bg: [string, string];
  art: ReactNode;
}

const INK = '#1d1420';

function Eye({ x, y, r = 3.4, fill = INK }: { x: number; y: number; r?: number; fill?: string }) {
  return (
    <g>
      <circle cx={x} cy={y} r={r} fill={fill} />
      <circle cx={x + r * 0.35} cy={y - r * 0.35} r={r * 0.36} fill="#fff" />
    </g>
  );
}

function Blush({ x, y }: { x: number; y: number }) {
  return <ellipse cx={x} cy={y} rx="4.2" ry="2.5" fill="#ff7fa3" opacity=".42" />;
}

function Smile({
  x = 48,
  y,
  w = 8,
  color = INK,
}: {
  x?: number;
  y: number;
  w?: number;
  color?: string;
}) {
  return (
    <path
      d={`M${x - w / 2} ${y}q${w / 2} ${w * 0.45} ${w} 0`}
      fill="none"
      stroke={color}
      strokeWidth="2.2"
      strokeLinecap="round"
    />
  );
}

function Stars({ points }: { points: [number, number, number][] }) {
  return (
    <g fill="#fff">
      {points.map(([x, y, r]) => (
        <circle key={`${x}-${y}`} cx={x} cy={y} r={r} opacity=".8" />
      ))}
    </g>
  );
}

/** Bust of one of our own heroes: hair, mask, suit, cape and chest emblem. */
function Hero(o: {
  skin: string;
  hair: string;
  hairStyle: 'short' | 'spiky' | 'long' | 'bun' | 'curly' | 'hood';
  suit: string;
  cape: string;
  mask: string;
  badge: string;
  emblem: ReactNode;
}) {
  const hair: Record<typeof o.hairStyle, ReactNode> = {
    short: <path d="M33 40q0-16 15-16t15 16q-6-8-15-8t-15 8z" fill={o.hair} />,
    spiky: (
      <path
        d="M32 41l-2-13 7 3 1-9 6 6 4-8 4 8 6-6 1 9 7-3-2 13q-8-10-16-10t-16 10z"
        fill={o.hair}
      />
    ),
    long: (
      <path
        d="M31 46q-1-22 17-22t17 22l2 18q-6-4-6-16-5-14-13-14t-13 14q0 12-6 16z"
        fill={o.hair}
      />
    ),
    bun: (
      <g fill={o.hair}>
        <circle cx="48" cy="20" r="6.5" />
        <path d="M33 40q0-16 15-16t15 16q-6-8-15-8t-15 8z" />
      </g>
    ),
    curly: (
      <g fill={o.hair}>
        {[
          [35, 34],
          [40, 28],
          [48, 26],
          [56, 28],
          [61, 34],
        ].map(([x, y]) => (
          <circle key={x} cx={x} cy={y} r="7" />
        ))}
      </g>
    ),
    hood: <path d="M27 54q0-32 21-32t21 32l-3 12q-4-26-18-28-14 2-18 28z" fill={o.hair} />,
  };
  return (
    // Drawn small, then enlarged from the bottom edge so the bust fills the tile.
    <g transform="translate(48 96) scale(1.14) translate(-48 -96)">
      <path d="M12 96l10-30q26-9 52 0l10 30z" fill={o.cape} />
      <path d="M21 96q0-26 27-28 27 2 27 28z" fill={o.suit} />
      <rect x="42" y="56" width="12" height="13" rx="4" fill={o.skin} />
      <ellipse cx="48" cy="45" rx="15.5" ry="17" fill={o.skin} />
      {hair[o.hairStyle]}
      <path d="M31.5 42q16.5-7 33 0l-1 7q-7 4-12.5-1l-3-2-3 2q-5.5 5-12.5 1z" fill={o.mask} />
      <ellipse cx="41" cy="45" rx="2.8" ry="2" fill="#fff" />
      <ellipse cx="55" cy="45" rx="2.8" ry="2" fill="#fff" />
      <path
        d="M44.5 54.5q3.5 2.4 7 0"
        fill="none"
        stroke={INK}
        strokeWidth="2"
        strokeLinecap="round"
      />
      <circle cx="48" cy="82" r="9.5" fill={o.badge} />
      {o.emblem}
    </g>
  );
}

const EMBLEM = {
  bolt: (c: string) => <path d="M50 73.5 42.5 83h5.5L45 91l8.5-10.5H48z" fill={c} />,
  flame: (c: string) => (
    <path d="M48 73c4 5 7 8 6 13a6 6 0 0 1-12 0c0-4 3-5 4-9 1 3 3 3 2-4z" fill={c} />
  ),
  snow: (c: string) => (
    <path d="M48 75v14m-6-11 12 8m0-8-12 8" stroke={c} strokeWidth="2.4" strokeLinecap="round" />
  ),
  star: (c: string) => (
    <path d="m48 74.5 2.3 4.8 5.2.7-3.8 3.6.9 5.2-4.6-2.5-4.6 2.5.9-5.2-3.8-3.6 5.2-.7z" fill={c} />
  ),
  moon: (c: string) => (
    <path d="M53 75.5a7.2 7.2 0 1 0 3.5 10.5 5.6 5.6 0 0 1-3.5-10.5z" fill={c} />
  ),
  peak: (c: string) => <path d="m40 88 6.5-10.5 3 4 3-5L57 88z" fill={c} />,
  comet: (c: string) => (
    <g fill={c}>
      <circle cx="51.5" cy="79" r="3.6" />
      <path d="m39.5 88.5 9-7.5" stroke={c} strokeWidth="2.6" strokeLinecap="round" opacity=".6" />
    </g>
  ),
  leaf: (c: string) => <path d="M41 89c0-10 6-14 14-14 0 8-5 14-14 14z" fill={c} />,
};

export const AVATAR_ART: Record<AvatarId, AvatarArt> = {
  // Cute animals
  raccoon: {
    label: 'Raccoon',
    bg: ['#dfe5ee', '#5a6577'],
    art: (
      <g>
        <circle cx="27" cy="32" r="10" fill="#7d838e" />
        <circle cx="27" cy="32" r="5.5" fill="#3a3d45" />
        <circle cx="69" cy="32" r="10" fill="#7d838e" />
        <circle cx="69" cy="32" r="5.5" fill="#3a3d45" />
        <ellipse cx="48" cy="56" rx="29" ry="26" fill="#adb3be" />
        <path d="M48 31v12" stroke="#3a3d45" strokeWidth="5" strokeLinecap="round" />
        <path
          d="M20 54c6-11 17-12 24-6 2 2 6 2 8 0 7-6 18-5 24 6-5 9-15 10-22 5q-3-1.5-6 0c-7 5-19 4-28-5z"
          fill="#2c2e35"
        />
        <circle cx="37" cy="53" r="4" fill="#fff" />
        <circle cx="37.6" cy="53.4" r="2.3" fill="#111" />
        <circle cx="59" cy="53" r="4" fill="#fff" />
        <circle cx="59.6" cy="53.4" r="2.3" fill="#111" />
        <ellipse cx="48" cy="69" rx="12" ry="9" fill="#f1f2f5" />
        <ellipse cx="48" cy="64.5" rx="4" ry="3" fill="#1d1f24" />
        <Smile y={70.5} w={7} />
      </g>
    ),
  },
  otter: {
    label: 'Otter',
    bg: ['#c4ecf3', '#2f7d8c'],
    art: (
      <g>
        <circle cx="28" cy="38" r="6.5" fill="#6e4429" />
        <circle cx="68" cy="38" r="6.5" fill="#6e4429" />
        <ellipse cx="48" cy="57" rx="27" ry="25" fill="#93603b" />
        <ellipse cx="48" cy="66" rx="19" ry="14" fill="#efd6b8" />
        <Eye x={39} y={52} />
        <Eye x={57} y={52} />
        <path d="M43.5 60h9a4.5 4.5 0 0 1-9 0z" fill="#2a1a12" />
        <path
          d="M48 64.5v2.5m-4 1.5q4 3 8 0"
          fill="none"
          stroke="#2a1a12"
          strokeWidth="2"
          strokeLinecap="round"
        />
        <path
          d="M35 63l-10-2m10 5-10 1m36-4 10-2m-10 5 10 1"
          stroke="#6e4429"
          strokeWidth="1.5"
          strokeLinecap="round"
          opacity=".7"
        />
        <Blush x={33} y={60} />
        <Blush x={63} y={60} />
      </g>
    ),
  },
  panda: {
    label: 'Panda',
    bg: ['#dcf3d4', '#4f8a55'],
    art: (
      <g>
        <circle cx="27" cy="31" r="10" fill="#1e2126" />
        <circle cx="69" cy="31" r="10" fill="#1e2126" />
        <ellipse cx="48" cy="55" rx="29" ry="26" fill="#fff" />
        <ellipse cx="37" cy="52" rx="7" ry="9.5" fill="#1e2126" transform="rotate(28 37 52)" />
        <ellipse cx="59" cy="52" rx="7" ry="9.5" fill="#1e2126" transform="rotate(-28 59 52)" />
        <circle cx="37.5" cy="51.5" r="3" fill="#fff" />
        <circle cx="38" cy="52" r="1.6" fill="#1e2126" />
        <circle cx="58.5" cy="51.5" r="3" fill="#fff" />
        <circle cx="59" cy="52" r="1.6" fill="#1e2126" />
        <ellipse cx="48" cy="62" rx="4.5" ry="3" fill="#1e2126" />
        <Smile y={67} w={7} />
        <Blush x={30} y={63} />
        <Blush x={66} y={63} />
      </g>
    ),
  },
  koala: {
    label: 'Koala',
    bg: ['#d8e8ff', '#4c6fb0'],
    art: (
      <g>
        <circle cx="24" cy="40" r="15" fill="#8e96a3" />
        <circle cx="24" cy="41" r="9" fill="#eceff4" />
        <circle cx="72" cy="40" r="15" fill="#8e96a3" />
        <circle cx="72" cy="41" r="9" fill="#eceff4" />
        <ellipse cx="48" cy="57" rx="26" ry="25" fill="#a9b1be" />
        <Eye x={37} y={52} r={3.2} />
        <Eye x={59} y={52} r={3.2} />
        <ellipse cx="48" cy="62" rx="7" ry="9" fill="#2e3138" />
        <ellipse cx="45.8" cy="58" rx="2" ry="3" fill="#fff" opacity=".3" />
        <Smile y={74} w={7} />
        <Blush x={31} y={63} />
        <Blush x={65} y={63} />
      </g>
    ),
  },
  'red-panda': {
    label: 'Red Panda',
    bg: ['#ffe4c9', '#c25a2c'],
    art: (
      <g>
        <path d="M21 42 25 20l17 10z" fill="#a8401a" />
        <path d="m26 37 3-11 8 5z" fill="#fff7ee" />
        <path d="M75 42 71 20 54 30z" fill="#a8401a" />
        <path d="m70 37-3-11-8 5z" fill="#fff7ee" />
        <ellipse cx="48" cy="56" rx="28" ry="25" fill="#cc5d28" />
        <ellipse cx="33" cy="61" rx="9" ry="8" fill="#fff7ee" />
        <ellipse cx="63" cy="61" rx="9" ry="8" fill="#fff7ee" />
        <ellipse cx="48" cy="66" rx="9.5" ry="7.5" fill="#fff7ee" />
        <ellipse cx="38" cy="43" rx="4" ry="2.5" fill="#fff7ee" />
        <ellipse cx="58" cy="43" rx="4" ry="2.5" fill="#fff7ee" />
        <path
          d="M38 56q-2 5 0 10m20-10q2 5 0 10"
          stroke="#7a2c12"
          strokeWidth="3"
          strokeLinecap="round"
          fill="none"
        />
        <Eye x={38} y={52} />
        <Eye x={58} y={52} />
        <ellipse cx="48" cy="62" rx="3.6" ry="2.6" fill="#2a140a" />
        <Smile y={67.5} w={6} />
      </g>
    ),
  },
  fox: {
    label: 'Fox',
    bg: ['#f1dcff', '#7b4fa8'],
    art: (
      <g>
        <path d="M20 46 24 16l20 16z" fill="#e2722e" />
        <path d="m25.5 38 2-15 9.5 8z" fill="#3a1d10" />
        <path d="M76 46 72 16 52 32z" fill="#e2722e" />
        <path d="m70.5 38-2-15-9.5 8z" fill="#3a1d10" />
        <path d="M17 48q31-26 62 0-2 22-31 36-29-14-31-36z" fill="#ec843d" />
        <path d="M17 50q15 4 31 34-24-10-31-34zm62 0q-15 4-31 34 24-10 31-34z" fill="#fff4ea" />
        <ellipse cx="48" cy="72" rx="8" ry="9" fill="#fff4ea" />
        <Eye x={38} y={53} />
        <Eye x={58} y={53} />
        <ellipse cx="48" cy="72" rx="3.6" ry="2.7" fill="#2a140a" />
        <Blush x={30} y={62} />
        <Blush x={66} y={62} />
      </g>
    ),
  },
  bunny: {
    label: 'Bunny',
    bg: ['#ffe2ee', '#e07aa5'],
    art: (
      <g>
        <g transform="rotate(-8 37 34)">
          <ellipse cx="37" cy="27" rx="7.5" ry="19" fill="#fff" />
          <ellipse cx="37" cy="29" rx="3.6" ry="13" fill="#ffb3cf" />
        </g>
        <g transform="rotate(8 59 34)">
          <ellipse cx="59" cy="27" rx="7.5" ry="19" fill="#fff" />
          <ellipse cx="59" cy="29" rx="3.6" ry="13" fill="#ffb3cf" />
        </g>
        <ellipse cx="48" cy="61" rx="25" ry="21" fill="#fff" />
        <Eye x={40} y={58} />
        <Eye x={56} y={58} />
        <path d="M45 64.5h6l-3 3z" fill="#ff8fb3" />
        <path
          d="M48 67.5q-2.5 3-5 1m5-1q2.5 3 5 1"
          fill="none"
          stroke={INK}
          strokeWidth="1.8"
          strokeLinecap="round"
        />
        <Blush x={34} y={66} />
        <Blush x={62} y={66} />
      </g>
    ),
  },
  penguin: {
    label: 'Penguin',
    bg: ['#d2efff', '#2c6fa8'],
    art: (
      <g>
        <ellipse cx="48" cy="60" rx="27" ry="29" fill="#1f2a3a" />
        <path
          d="M48 41c-8-8-22-4-21 9 1 14 9 25 21 27 12-2 20-13 21-27 1-13-13-17-21-9z"
          fill="#fff"
        />
        <Eye x={40} y={53} />
        <Eye x={56} y={53} />
        <path d="M43 59.5h10l-5 6z" fill="#f5a623" />
        <Blush x={34} y={62} />
        <Blush x={62} y={62} />
      </g>
    ),
  },
  cat: {
    label: 'Cat',
    bg: ['#fff1c9', '#d99a2b'],
    art: (
      <g>
        <path d="M22 46 26 20l16 14z" fill="#e3923a" />
        <path d="m27 40 2-12 7 6z" fill="#ffd0b0" />
        <path d="M74 46 70 20 54 34z" fill="#e3923a" />
        <path d="m69 40-2-12-7 6z" fill="#ffd0b0" />
        <ellipse cx="48" cy="58" rx="27" ry="24" fill="#f0a24a" />
        <path d="M44 37v7m4-8v8m4-7v7" stroke="#c26f1f" strokeWidth="2.5" strokeLinecap="round" />
        <Eye x={38} y={55} r={3.6} fill="#2d3b1c" />
        <Eye x={58} y={55} r={3.6} fill="#2d3b1c" />
        <path d="M45.5 62h5L48 65z" fill="#e86a8a" />
        <path
          d="M48 65q-3 3-6 1m6-1q3 3 6 1"
          fill="none"
          stroke="#6b3a10"
          strokeWidth="1.8"
          strokeLinecap="round"
        />
        <path
          d="M33 62l-12-2m12 5-12 2m42-5 12-2m-12 5 12 2"
          stroke="#fff"
          strokeWidth="1.5"
          strokeLinecap="round"
          opacity=".85"
        />
      </g>
    ),
  },
  owl: {
    label: 'Owl',
    bg: ['#d2cdf5', '#3d3480'],
    art: (
      <g>
        <path d="m22 34 10 6h32l10-6v28a26 24 0 0 1-52 0z" fill="#8a6440" />
        <ellipse cx="48" cy="72" rx="15" ry="11" fill="#e9d6b9" />
        <circle cx="37" cy="52" r="11" fill="#f6ead6" />
        <circle cx="59" cy="52" r="11" fill="#f6ead6" />
        <Eye x={37} y={52} r={5.5} fill="#1d1410" />
        <Eye x={59} y={52} r={5.5} fill="#1d1410" />
        <path d="M44 61h8l-4 7z" fill="#f2a23a" />
        <path d="m28 41 11 4m29-4-11 4" stroke="#5a3d22" strokeWidth="3" strokeLinecap="round" />
      </g>
    ),
  },
  hedgehog: {
    label: 'Hedgehog',
    bg: ['#fbecd0', '#a7743c'],
    art: (
      <g>
        <path
          d="m14 64 4-16-4-7 8-4-2-10 10 2 2-10 8 6 6-10 6 8 8-6 2 10 10-2v10l10 2-4 8 6 8-6 6 4 8z"
          fill="#7a5232"
        />
        <ellipse cx="48" cy="63" rx="24" ry="20" fill="#f2dcc0" />
        <Eye x={40} y={60} />
        <Eye x={56} y={60} />
        <circle cx="48" cy="67.5" r="3.5" fill="#2a1a12" />
        <Smile y={72.5} w={6} />
        <Blush x={34} y={68} />
        <Blush x={62} y={68} />
      </g>
    ),
  },
  sloth: {
    label: 'Sloth',
    bg: ['#cff3e8', '#3a8f7a'],
    art: (
      <g>
        <ellipse cx="48" cy="57" rx="28" ry="26" fill="#9b8268" />
        <path d="M39 34q4.5-7 9 0 4.5-7 9 0z" fill="#9b8268" />
        <ellipse cx="48" cy="59" rx="22" ry="19" fill="#eadcc6" />
        <ellipse cx="37" cy="55" rx="8.5" ry="5" fill="#4a3828" transform="rotate(-20 37 55)" />
        <ellipse cx="59" cy="55" rx="8.5" ry="5" fill="#4a3828" transform="rotate(20 59 55)" />
        <Eye x={38} y={55} r={2.6} fill="#120c08" />
        <Eye x={58} y={55} r={2.6} fill="#120c08" />
        <ellipse cx="48" cy="63" rx="4" ry="3" fill="#3a2a1c" />
        <Smile y={68} w={12} color="#3a2a1c" />
      </g>
    ),
  },

  // Our own superheroes
  'hero-bolt': {
    label: 'Bolt',
    bg: ['#d3e3ff', '#2b4aa8'],
    art: (
      <Hero
        skin="#f1c7a3"
        hair="#2b1d14"
        hairStyle="spiky"
        suit="#2d5bd8"
        cape="#f2c230"
        mask="#1b2c6b"
        badge="#f2c230"
        emblem={EMBLEM.bolt('#2d5bd8')}
      />
    ),
  },
  'hero-blaze': {
    label: 'Blaze',
    bg: ['#ffdcc6', '#c2391f'],
    art: (
      <Hero
        skin="#8d5a3b"
        hair="#1a1210"
        hairStyle="curly"
        suit="#d8352a"
        cape="#ff9a2e"
        mask="#7a1410"
        badge="#ffd166"
        emblem={EMBLEM.flame('#d8352a')}
      />
    ),
  },
  'hero-frost': {
    label: 'Frost',
    bg: ['#e4f7ff', '#3b8fc2'],
    art: (
      <Hero
        skin="#f6dccb"
        hair="#eef8ff"
        hairStyle="long"
        suit="#4fb3e8"
        cape="#1d5f8a"
        mask="#1d5f8a"
        badge="#fff"
        emblem={EMBLEM.snow('#3b8fc2')}
      />
    ),
  },
  'hero-nova': {
    label: 'Nova',
    bg: ['#eedcff', '#6a32b8'],
    art: (
      <Hero
        skin="#c98b62"
        hair="#3a1c4a"
        hairStyle="bun"
        suit="#7a3fd1"
        cape="#ff6fb5"
        mask="#2e1452"
        badge="#ffe27a"
        emblem={EMBLEM.star('#7a3fd1')}
      />
    ),
  },
  'hero-shade': {
    label: 'Shade',
    bg: ['#cdd3ee', '#2c3352'],
    art: (
      <Hero
        skin="#e9b48c"
        hair="#262a3a"
        hairStyle="hood"
        suit="#3a4160"
        cape="#1a1d29"
        mask="#0f111a"
        badge="#c9d1ff"
        emblem={EMBLEM.moon('#262a3a')}
      />
    ),
  },
  'hero-titan': {
    label: 'Titan',
    bg: ['#d6f4df', '#2b7a4b'],
    art: (
      <Hero
        skin="#6b4630"
        hair="#15100c"
        hairStyle="short"
        suit="#2f9a5a"
        cape="#1f5f3a"
        mask="#123d25"
        badge="#e8f7ec"
        emblem={EMBLEM.peak('#2f9a5a')}
      />
    ),
  },
  'hero-comet': {
    label: 'Comet',
    bg: ['#d2f6f0', '#16857b'],
    art: (
      <Hero
        skin="#f3d0b5"
        hair="#e8762b"
        hairStyle="short"
        suit="#1fa59a"
        cape="#ffd166"
        mask="#0f5a54"
        badge="#fff"
        emblem={EMBLEM.comet('#1fa59a')}
      />
    ),
  },
  'hero-bloom': {
    label: 'Bloom',
    bg: ['#ffe2ee', '#d4517f'],
    art: (
      <Hero
        skin="#d9a07c"
        hair="#8b2f5a"
        hairStyle="long"
        suit="#ff7aa8"
        cape="#3fa66b"
        mask="#8b1f4a"
        badge="#fff0f6"
        emblem={EMBLEM.leaf('#3fa66b')}
      />
    ),
  },

  // Films: nods to a story or a genre
  'rock-alien': {
    label: 'Rock Alien',
    bg: ['#6a60b8', '#191536'],
    art: (
      <g>
        <Stars
          points={[
            [34, 12, 0.9],
            [62, 10, 1.1],
            [88, 44, 0.9],
            [8, 50, 1],
            [88, 88, 0.8],
          ]}
        />
        <g
          fill="none"
          stroke="#7d7266"
          strokeWidth="7"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M36 54 22 62l-4 22m42-30 14 8 4 22M42 60l-6 14 2 14m16-28 6 14-2 14M58 40l12-12 10 2" />
        </g>
        <g fill="#b0a495">
          <circle cx="22" cy="62" r="3.8" />
          <circle cx="74" cy="62" r="3.8" />
          <circle cx="36" cy="74" r="3.8" />
          <circle cx="60" cy="74" r="3.8" />
          <circle cx="70" cy="28" r="3.8" />
        </g>
        <path d="m28 48 8-18 22-3 12 13-5 18-25 3z" fill="#b0a495" />
        <path d="m36 30 12 14-20 4z" fill="#fff" opacity=".16" />
        <path d="m48 44 17 14-25 3z" fill="#000" opacity=".14" />
        <path
          d="m36 30 12 14 10-17M48 44l-8 17m8-17 22-4m-22 4-20 4"
          fill="none"
          stroke="#8a7f72"
          strokeWidth="1.8"
          strokeLinejoin="round"
        />
        <g fill="#ffe08a">
          <path
            d="M16 32V20l8-2v12"
            fill="none"
            stroke="#ffe08a"
            strokeWidth="2.4"
            strokeLinejoin="round"
          />
          <circle cx="14" cy="32" r="3" />
          <circle cx="22" cy="30" r="3" />
          <path d="M86 22v-9" stroke="#ffe08a" strokeWidth="2.2" strokeLinecap="round" />
          <circle cx="84" cy="22" r="2.6" />
        </g>
      </g>
    ),
  },
  astronaut: {
    label: 'Astronaut',
    bg: ['#ffdcb0', '#c4552b'],
    art: (
      <g>
        <circle cx="76" cy="20" r="6.5" fill="#fff3c4" />
        <path d="M17 96q1-25 31-27 30 2 31 27z" fill="#f4f1ec" />
        <rect x="39" y="79" width="18" height="11" rx="2.5" fill="#cfd6e0" />
        <circle cx="44" cy="84.5" r="1.8" fill="#e5484d" />
        <circle cx="50" cy="84.5" r="1.8" fill="#3b82f6" />
        <circle cx="26" cy="83" r="4.5" fill="#ff7a3d" />
        <circle cx="48" cy="46" r="25" fill="#f4f1ec" />
        <circle cx="48" cy="46" r="25" fill="none" stroke="#d6d2ca" strokeWidth="2" />
        <ellipse cx="48" cy="47" rx="17.5" ry="14.5" fill="#24324a" />
        <path
          d="M36 43q6-8 17-7"
          fill="none"
          stroke="#8fb3ff"
          strokeWidth="3"
          strokeLinecap="round"
          opacity=".7"
        />
        <circle cx="57" cy="52" r="2.6" fill="#ffb26b" opacity=".85" />
      </g>
    ),
  },
  starship: {
    label: 'Starship',
    bg: ['#3a5a9c', '#0c1226'],
    art: (
      <g>
        <Stars
          points={[
            [12, 14, 1.2],
            [26, 30, 0.8],
            [80, 12, 1],
            [86, 70, 1.1],
            [16, 78, 0.9],
            [62, 86, 0.8],
          ]}
        />
        <g transform="rotate(-35 48 48)">
          <path d="M22 44 8 48l14 4z" fill="#ffb347" />
          <path d="M22 45 14 48l8 3z" fill="#fff3c4" />
          <rect x="22" y="41" width="48" height="14" rx="6" fill="#e8edf5" />
          <path d="M70 41q14 7 0 14z" fill="#e8edf5" />
          <rect x="30" y="41" width="3" height="14" fill="#c3cddb" />
          <ellipse cx="44" cy="48" rx="5" ry="15" fill="none" stroke="#9fb3d9" strokeWidth="3.2" />
          <circle cx="58" cy="48" r="2.6" fill="#6fc3ff" />
          <circle cx="65" cy="48" r="2" fill="#6fc3ff" />
        </g>
      </g>
    ),
  },
  dinosaur: {
    label: 'Dinosaur',
    bg: ['#ddf6cc', '#4f9a3f'],
    art: (
      <g>
        <path d="M20 84 22 52q2-22 24-24l20 2q14 2 14 14v10q0 6-8 6H50l-4 24z" fill="#6cc04a" />
        <path d="M50 60h22q4 0 4 4l-26 2z" fill="#9ad97c" />
        <path d="m54 60 3 4 3-4 3 4 3-4 3 4 3-4z" fill="#fff" />
        <Eye x={58} y={41} />
        <circle cx="74" cy="39" r="1.6" fill="#2b4a22" />
        <circle cx="33" cy="46" r="3.2" fill="#4f9a3f" />
        <circle cx="30" cy="58" r="2.4" fill="#4f9a3f" />
        <circle cx="39" cy="38" r="2.2" fill="#4f9a3f" />
        <path
          d="M40 72q6 0 8 4"
          fill="none"
          stroke="#4f9a3f"
          strokeWidth="4"
          strokeLinecap="round"
        />
        <Blush x={64} y={52} />
      </g>
    ),
  },
  wizard: {
    label: 'Wizard',
    bg: ['#dcd6ff', '#3c2f8f'],
    art: (
      <g>
        <ellipse cx="48" cy="54" rx="15" ry="13" fill="#f2c9a8" />
        <path d="M30 54q2 34 18 38 16-4 18-38-8 10-18 10t-18-10z" fill="#f4f4f8" />
        <path d="M38 61q5-4 10 0 5-4 10 0-5 4-10 1-5 3-10-1z" fill="#e2e2ea" />
        <Eye x={42} y={52} r={2.6} />
        <Eye x={54} y={52} r={2.6} />
        <ellipse cx="48" cy="57" rx="3" ry="2.5" fill="#e3a989" />
        <path d="M24 45 50 8l24 37z" fill="#4a3aa8" />
        <ellipse cx="48" cy="45" rx="31" ry="6" fill="#3a2c8c" />
        <rect x="31" y="37" width="35" height="5" rx="2" fill="#ffe27a" opacity=".85" />
        <path
          d="m53 20 1.4 3 3.2.4-2.3 2.2.6 3.2-2.9-1.6-2.9 1.6.6-3.2-2.3-2.2 3.2-.4z"
          fill="#ffe27a"
        />
        <circle cx="42" cy="30" r="1.6" fill="#ffe27a" />
      </g>
    ),
  },
  robot: {
    label: 'Robot',
    bg: ['#ffe5bd', '#d27b2a'],
    art: (
      <g>
        <path d="M30 96V77q0-6 6-6h24q6 0 6 6v19z" fill="#b8c4d2" />
        <circle cx="48" cy="83" r="4" fill="#ff5d5d" />
        <path d="M48 19v10" stroke="#5d6b7a" strokeWidth="3" strokeLinecap="round" />
        <circle cx="48" cy="16" r="4.2" fill="#ff5d5d" />
        <rect x="17" y="40" width="7" height="17" rx="3" fill="#9aa8b8" />
        <rect x="72" y="40" width="7" height="17" rx="3" fill="#9aa8b8" />
        <rect x="23" y="28" width="50" height="42" rx="13" fill="#cfd8e3" />
        <rect x="30" y="35" width="36" height="27" rx="8" fill="#1f2a38" />
        <circle cx="40" cy="46" r="4.2" fill="#6ff0e0" />
        <circle cx="56" cy="46" r="4.2" fill="#6ff0e0" />
        <path
          d="M41 54q7 5 14 0"
          fill="none"
          stroke="#6ff0e0"
          strokeWidth="2.6"
          strokeLinecap="round"
        />
      </g>
    ),
  },
  shark: {
    label: 'Shark',
    bg: ['#c6ecff', '#1f6fa8'],
    art: (
      <g>
        <path d="M0 80q12-6 24 0t24 0 24 0 24 0v16H0z" fill="#1a5f92" opacity=".55" />
        <path d="M14 70q6-30 36-34l8-16 6 18q18 6 20 24-14 12-36 12T14 70z" fill="#8fa9bd" />
        <path d="M24 66q24 10 52-2-6 10-28 10-18 0-24-8z" fill="#eef4f8" />
        <path d="M40 52l-2 8m6-8-2 8" stroke="#6f8aa0" strokeWidth="2" strokeLinecap="round" />
        <Eye x={62} y={51} r={3.2} />
        <path d="M50 62q12 4 22-2" fill="none" stroke={INK} strokeWidth="2" strokeLinecap="round" />
        <path d="m54 63 2 3 2-3zm6 0.6 2 3 2-3zm6-1 2 3 2-3z" fill="#fff" />
        <Blush x={68} y={56} />
      </g>
    ),
  },
  pirate: {
    label: 'Pirate',
    bg: ['#ffe2bd', '#8a4b22'],
    art: (
      <g>
        <ellipse cx="48" cy="56" rx="20" ry="20" fill="#e9b48c" />
        <path d="M30 60q2 22 18 22t18-22q-6 10-18 10t-18-10z" fill="#6b3a1f" />
        <Smile y={66} w={11} color="#3a1a0c" />
        <path d="m28 46 40 12" stroke="#1a1412" strokeWidth="2.4" />
        <ellipse cx="58" cy="52" rx="6.2" ry="5.2" fill="#1a1412" />
        <Eye x={39} y={52} />
        <circle cx="28" cy="62" r="2.6" fill="none" stroke="#f2c230" strokeWidth="1.6" />
        <path d="M28 37q20-30 40 0z" fill="#2a2220" />
        <path d="M17 41q31-26 62 0-13-5-31-5t-31 5z" fill="#2a2220" />
        <circle cx="48" cy="26" r="3.6" fill="#f4efe6" />
        <path d="m43 31 10 0" stroke="#f4efe6" strokeWidth="1.8" strokeLinecap="round" />
      </g>
    ),
  },

  // TV: nods to a show or a genre
  chemist: {
    label: 'Chemist',
    bg: ['#d8f2ec', '#2c7a6c'],
    art: (
      <g>
        <path d="M17 96q1-26 31-28 30 2 31 28z" fill="#f5d23a" />
        <path d="M48 74v22" stroke="#c9a21c" strokeWidth="2" />
        <circle cx="48" cy="47" r="25" fill="#f5d23a" />
        <path d="M28 36q20-14 40 0" fill="none" stroke="#c9a21c" strokeWidth="2" opacity=".6" />
        <ellipse cx="48" cy="51" rx="17" ry="18" fill="#f2c9a8" />
        <rect x="33" y="38" width="30" height="13" rx="6.5" fill="#2b3440" />
        <circle cx="41" cy="44.5" r="4.6" fill="#8fd3ff" />
        <circle cx="55" cy="44.5" r="4.6" fill="#8fd3ff" />
        <circle cx="42.4" cy="43" r="1.4" fill="#fff" />
        <circle cx="56.4" cy="43" r="1.4" fill="#fff" />
        <path d="M34 55q14-8 28 0l-4 12q-10 6-20 0z" fill="#3a434f" />
        <circle cx="34" cy="63" r="6.2" fill="#e45b8f" />
        <circle cx="62" cy="63" r="6.2" fill="#e45b8f" />
        <circle cx="48" cy="63" r="3" fill="#2b3440" />
      </g>
    ),
  },
  mastermind: {
    label: 'Mastermind',
    bg: ['#d2ead9', '#2f6a4a'],
    art: (
      <g>
        <path d="M15 96q2-24 33-26 31 2 33 26z" fill="#22262b" />
        <path d="m42 70 6 12 6-12z" fill="#dcd6c8" />
        <rect x="43" y="59" width="10" height="13" rx="3" fill="#e2b998" />
        <ellipse cx="48" cy="48" rx="16" ry="18" fill="#ecc6a5" />
        <path d="M41 58q7 12 14 0-3 6-7 6t-7-6z" fill="#6b5a48" />
        <path
          d="M42 57q6-3 12 0"
          fill="none"
          stroke="#6b5a48"
          strokeWidth="2.6"
          strokeLinecap="round"
        />
        <g fill="#ffffff22" stroke="#15171a" strokeWidth="2">
          <rect x="35.5" y="43" width="10.5" height="7" rx="2" />
          <rect x="50" y="43" width="10.5" height="7" rx="2" />
        </g>
        <path d="M46 46h4" stroke="#15171a" strokeWidth="2" />
        <ellipse cx="48" cy="35" rx="25" ry="5" fill="#15171a" />
        <path d="M32 35q0-15 16-15t16 15z" fill="#15171a" />
        <rect x="32" y="29.5" width="32" height="4" fill="#2d3238" />
      </g>
    ),
  },
  'desert-rv': {
    label: 'Desert RV',
    bg: ['#ffdba8', '#e07b3c'],
    art: (
      <g>
        <circle cx="66" cy="34" r="12" fill="#ffe9b0" />
        <path d="M0 74q48-8 96 0v22H0z" fill="#d99a5a" />
        <path d="m6 74 6-16h18l4 16z" fill="#b8703a" />
        <g fill="#4f8a3f">
          <rect x="80" y="52" width="6" height="24" rx="3" />
          <rect x="86" y="58" width="5" height="4" rx="2" />
          <rect x="88" y="52" width="4" height="10" rx="2" />
        </g>
        <rect x="20" y="52" width="48" height="22" rx="4" fill="#f4f1e6" />
        <rect x="20" y="64" width="48" height="5" fill="#8a5a3a" />
        <path d="M68 57h6l5 9v8H68z" fill="#f4f1e6" />
        <path d="M69 59h4l3.5 6H69z" fill="#6fb6d8" />
        <rect x="26" y="56" width="9" height="6" rx="1" fill="#6fb6d8" />
        <rect x="39" y="56" width="9" height="6" rx="1" fill="#6fb6d8" />
        <rect x="53" y="56" width="7" height="14" rx="1" fill="#d8d2c0" />
        <circle cx="31" cy="76" r="5.5" fill="#2a2523" />
        <circle cx="31" cy="76" r="2" fill="#b8b2a8" />
        <circle cx="66" cy="76" r="5.5" fill="#2a2523" />
        <circle cx="66" cy="76" r="2" fill="#b8b2a8" />
      </g>
    ),
  },
  dragon: {
    label: 'Dragon',
    bg: ['#ffd4cc', '#b8333a'],
    art: (
      <g>
        <path d="M31 34 24 14l15 13z" fill="#f4e2c0" />
        <path d="m65 34 7-20-15 13z" fill="#f4e2c0" />
        <path d="m38 31 4-7 4 7 4-7 4 7 4-7 4 7z" fill="#f2a23a" />
        <ellipse cx="48" cy="51" rx="27" ry="22" fill="#3fa76b" />
        <ellipse cx="48" cy="64" rx="18" ry="12" fill="#5cc488" />
        <circle cx="42" cy="61" r="2" fill="#1f5a3a" />
        <circle cx="54" cy="61" r="2" fill="#1f5a3a" />
        <Eye x={37} y={47} />
        <Eye x={59} y={47} />
        <path d="m30 40 9 3m27-3-9 3" stroke="#1f5a3a" strokeWidth="2.4" strokeLinecap="round" />
        <Smile y={68} w={8} color="#1f5a3a" />
        <circle cx="78" cy="58" r="4.5" fill="#fff" opacity=".7" />
        <circle cx="85" cy="52" r="3.2" fill="#fff" opacity=".55" />
      </g>
    ),
  },
  zombie: {
    label: 'Zombie',
    bg: ['#ddd3f3', '#4a3f78'],
    art: (
      <g>
        <path d="M23 96q2-18 25-18t25 18z" fill="#6a5a9a" />
        <path d="m38 78 4 6 3-6m6 0 3 6 4-6" fill="none" stroke="#4a3d78" strokeWidth="2" />
        <ellipse cx="48" cy="52" rx="24" ry="26" fill="#9cc98a" />
        <path d="M25 46q-2-22 23-22t23 22l-6-10-6 6-6-8-6 8-5-8-6 8-5-6z" fill="#3a3a2a" />
        <Eye x={39} y={52} />
        <circle cx="58" cy="52" r="5" fill="#fff" />
        <circle cx="59.5" cy="53" r="2" fill={INK} />
        <path
          d="m29 62 10-4m-7-1 1.5 4m2.5-5 1.5 4"
          stroke="#4a6a3a"
          strokeWidth="1.8"
          strokeLinecap="round"
        />
        <path d="M40 68h16" stroke="#2a3a20" strokeWidth="2.4" strokeLinecap="round" />
        <rect x="45" y="68" width="4" height="3.5" rx="0.8" fill="#fff" />
      </g>
    ),
  },
  detective: {
    label: 'Detective',
    bg: ['#ece2cd', '#7a5a36'],
    art: (
      <g>
        <path d="M15 96q2-24 33-26 31 2 33 26z" fill="#9a7a52" />
        <path d="m36 72 12 14 12-14-6 0-6 6-6-6z" fill="#7d6040" />
        <ellipse cx="48" cy="53" rx="16" ry="17" fill="#f0caa8" />
        <Eye x={42} y={54} r={2.8} />
        <Eye x={54} y={54} r={2.8} />
        <Smile y={62} w={7} />
        <path d="M30 45q0-21 18-21t18 21z" fill="#a8875c" />
        <path
          d="M36 28v16m8-19v19m8-19v19m8-16v16M31 36h34"
          stroke="#8a6a42"
          strokeWidth="1.4"
          opacity=".75"
        />
        <path d="M33 45q15 7 30 0-15 3-30 0z" fill="#8a6a42" />
        <circle cx="48" cy="23" r="2.6" fill="#8a6a42" />
        <circle cx="71" cy="73" r="9" fill="#cfe9ff" opacity=".65" />
        <circle cx="71" cy="73" r="9" fill="none" stroke="#3a2a1a" strokeWidth="3" />
        <path d="m64 80-8 10" stroke="#3a2a1a" strokeWidth="4.2" strokeLinecap="round" />
      </g>
    ),
  },
  monarch: {
    label: 'Monarch',
    bg: ['#ecdcff', '#5b2f8f'],
    art: (
      <g>
        <path d="M15 96q2-24 33-26 31 2 33 26z" fill="#7a2a5a" />
        <path d="M23 82q25-14 50 0v7q-25-13-50 0z" fill="#fff" />
        <g fill="#1d1420">
          <circle cx="33" cy="81" r="1" />
          <circle cx="48" cy="77" r="1" />
          <circle cx="63" cy="81" r="1" />
        </g>
        <ellipse cx="48" cy="53" rx="16" ry="17" fill="#f2d0b5" />
        <path d="M31 54q-1-22 17-22t17 22q-3-12-17-14-14 2-17 14z" fill="#c9c2b8" />
        <Eye x={42} y={54} r={2.8} />
        <Eye x={54} y={54} r={2.8} />
        <Smile y={62} w={7} color="#a3425a" />
        <g fill="#f4efe6">
          {[40, 44, 48, 52, 56].map((x) => (
            <circle
              key={x}
              cx={x}
              cy={x === 48 ? 72 : x === 44 || x === 52 ? 71.4 : 70.2}
              r="1.5"
            />
          ))}
        </g>
        <path d="m32 35 2-17 7 8 7-12 7 12 7-8 2 17z" fill="#f2c230" />
        <rect x="32" y="31" width="32" height="5" rx="1.5" fill="#e0a81c" />
        <circle cx="48" cy="33.5" r="2" fill="#d8355f" />
        <circle cx="40" cy="33.5" r="1.6" fill="#3a7bd5" />
        <circle cx="56" cy="33.5" r="1.6" fill="#3a7bd5" />
      </g>
    ),
  },
  knight: {
    label: 'Knight',
    bg: ['#ffe8cf', '#a8552a'],
    art: (
      <g>
        <path d="M17 96q2-21 31-22 29 1 31 22z" fill="#8f99a5" />
        <path d="M42 74h12v22H42z" fill="#d8352a" />
        <path d="M48 23q2-14 16-14-8 6-10 15z" fill="#d8352a" />
        <path d="M26 50q0-28 22-28t22 28v20q-22 10-44 0z" fill="#b9c2cc" />
        <path d="M48 22v50" stroke="#9aa4af" strokeWidth="2" />
        <rect x="31" y="44" width="34" height="5.5" rx="2.5" fill="#22272e" />
        <g fill="#22272e">
          <circle cx="40" cy="58" r="1.4" />
          <circle cx="44" cy="61" r="1.4" />
          <circle cx="52" cy="61" r="1.4" />
          <circle cx="56" cy="58" r="1.4" />
        </g>
        <path
          d="M30 36q8-8 18-8"
          fill="none"
          stroke="#fff"
          strokeWidth="2.4"
          strokeLinecap="round"
          opacity=".55"
        />
      </g>
    ),
  },
};
